import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {Button, setOptions, Snackbar, Toast, alert} from "@mobiscroll/react";
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import {BalloonEditor, Autosave, BlockQuote, BlockToolbar, Bold, Essentials, Italic, Paragraph, RemoveFormat, Subscript, Superscript, Underline, CodeBlock} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import '../styles/dashboardQuestion.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faPaperPlane,
    faObjectsColumn,
    faSliders,
    faPlusMinus,
    faMessageLines,
    faArrowDownFromLine,
    faArrowUpToLine
} from "@fortawesome/pro-duotone-svg-icons";
import Empty from "../utils/Empty";
import DashboardQuestionSettings from "../dialogs/DashboardQuestionSettings";
import DashboardQuestionSave from "../dialogs/DashboardQuestionSave";
import DashboardQuestionUsers from "../dialogs/DashboardQuestionUsers";
import {NetworkContext} from "../utils/NetworkContext";

setOptions({
    theme: 'ios',
    themeVariant: 'light',
});

function DashboardQuestion({width, configuration, Dialog, dialog, toast, system, locale, setModel, model, functions, setFunctions, text, setText, setToast, setMessages, Navigation, messages, setSystem, setWorkspace, workspace, stack, setStack, navigation, teamtalk, setTeamtalk, Conversation, user, setThinking, setViewConversation, tokensUsed, setTokensUsed, folded, setFolded, sort, setAvatar, setBroadcast, setLocked, setLabel}) {
    const {wsCall, wsResponse, setWsResponse} = useContext(NetworkContext);
    const [editorInstance, setEditorInstance] = useState(null);
    const broadcast = useRef(null);
    const [active, setActive] = useState(null);
    const [iconHover, setIconHover] = useState(null);
    const [usageTier, setUsageTier] = useState('success');
    const [quota, setQuota] = useState(false);
    const [memoryUsage, setMemoryUsage] = useState(null);
    const [memoryPercentage, setMemoryPercentage] = useState(null);
    const [contextLimit, setContextLimit] = useState(null);
    const [memoryIndicator, setMemoryIndicator] = useState(false);

    const ButtonMessage = () => {
        if (navigation.card) {
            Navigation({card: 'close'});
        }
        setFolded(true);
        setViewConversation(false);
        setThinking(Date.now());
        setToast({message: locale.dashboardQuestion.ai, color: 'info', display: 'bottom', duration: false});
        setMemoryIndicator(false);

        const message = {
            type: 'wss',
            path: 'conversation',
            action: 'update',
            data: {
                text: text,
                memoryPercentage: memoryPercentage,
                model: model,
                functions: functions,
                teamtalk:  (!Empty(teamtalk) && teamtalk.id) ? {id: teamtalk.id, users: teamtalk.users} : null
            }
        };
        wsCall(message);
    }

    const ButtonQuestion = () => {
        setFolded(false);
    }

    const ButtonFold = () => {
        if (folded) {
            setFolded(false);
        } else {
            setFolded(true);
        }
    }

    const ButtonWorkspace = () => {
        Dialog({view: 'dashboardWorkspace', tab: 'workspace'});
    }

    const ButtonSystem = () => {
        setSystem(false);

        const message = {
            type: 'wss',
            path: 'conversation',
            action: 'get',
            data: {
                system: false,
                rewind: false,
                error: false
            }
        };
        wsCall(message);
    }

    const Avatar = () => {
        if (teamtalk.avatar) {

            return "url(" + teamtalk.avatar + ")";
        } else if (!teamtalk.avatar && (teamtalk.voice === 'nova' || teamtalk.voice === 'shimmer')) {

            return "url(" + (configuration.avatar.female) + ")";
        } else if (!teamtalk.avatar && !(teamtalk.voice === 'nova' || teamtalk.voice === 'shimmer')) {

            return "url(" + (configuration.avatar.male) + ")";
        }
    }

    const EditorReady = editor => {
        setEditorInstance(editor);
    };

    const EditorUpdate = data => {
        let content = "<p></p>";

        if (data) {
            content = data
        }
        setText(content);
        MemoryUsage(content);

        if (!Empty(teamtalk) && teamtalk.id && broadcast.current) {
            broadcast.current = null;

        } else if (!Empty(teamtalk) && teamtalk.id && !broadcast.current) {

            const message = {
                type: 'wss',
                path: 'teamtalk-question',
                action: 'broadcast',
                data: {
                    text: content,
                    teamtalk:  {id: teamtalk.id, users: teamtalk.users}
                }
            };
            wsCall(message);
        } else if (Empty(teamtalk)) {
            window.localStorage.text = content;
        }
    }

    const EstimateTokens = text => {
        if (!text || text.length === 0) return 0;

        const encoder = new TextEncoder();
        const bytes = encoder.encode(text).length;
        const len = text.length;

        const symbolMatches = text.match(/[\[\]{}(),.:;'"`<>+=\-*/\\|&^%!~?#@$]/g);
        const whitespaceMatches = text.match(/\s/g);

        const symbols = symbolMatches ? symbolMatches.length : 0;
        const whitespace = whitespaceMatches ? whitespaceMatches.length : 0;

        const symbolRatio = symbols / len;
        const whitespaceRatio = whitespace / len;

        let bytesPerToken;
        if (symbolRatio > 0.2) {
            bytesPerToken = 3.0;
        } else if (symbolRatio > 0.12) {
            bytesPerToken = 3.4;
        } else {
            bytesPerToken = 4.0;
        }

        bytesPerToken *= 1.0 + Math.min(0.1, whitespaceRatio * 0.2);

        const raw = Math.ceil(bytes / bytesPerToken);
        return Math.ceil(raw * 1.1);
    }

    const MemoryUsage = text => {
        if (!text || text.length <= 8) {
            setMemoryIndicator(false);

            return;
        }

        const estimatedTokens = EstimateTokens(text);
        const remainingTokens = contextLimit - tokensUsed;
        const blockThreshold = remainingTokens * 0.5;

        const percentage = Math.min(
            parseFloat((((tokensUsed + estimatedTokens) / contextLimit) * 100).toFixed(1)),
            100
        );

        const inputPercentage = (estimatedTokens / remainingTokens) * 100;
        let newTier;
        if (inputPercentage >= 20) {
            newTier = "danger";
        } else if (inputPercentage >= 10) {
            newTier = "warning";
        } else {
            newTier = "success";
        }

        const shouldBlock = estimatedTokens > blockThreshold;

        const message = shouldBlock
            ? locale.dashboardQuestion.ak
            : locale.dashboardQuestion.al + ': ' + percentage + '%';

        if (newTier !== usageTier) {
            setUsageTier(newTier);
        }

        setMemoryUsage(message);
        setMemoryPercentage(percentage);

        if (!memoryIndicator) {
            setMemoryIndicator(true);
        }

        if (shouldBlock !== quota) {
            setQuota(shouldBlock);
        }
    }

    const AvatarStyle = data => {
        if (data.type === 'text' && width > 768 && (!active || active !== data.user)) {
            return "cs-dashboard-question-avatar-text mbsc-align-center mbsc-txt-m mbsc-bold mbsc-txt-muted";
        } else if (data.type === 'text' && width > 768 && active && active === data.user) {
            return "cs-dashboard-question-avatar-text cs-dashboard-question-avatar-active mbsc-align-center mbsc-txt-m mbsc-bold mbsc-txt-muted";
        } else if (data.type === 'text' && width < 768 && (!active || active !== data.user)) {
            return "cs-dashboard-question-avatar-text-xs mbsc-align-center mbsc-txt-s mbsc-bold mbsc-txt-muted";
        } else if (data.type === 'text' && width < 768 && active && active === data.user) {
            return "cs-dashboard-question-avatar-text-xs cs-dashboard-question-avatar-active-xs cs-dashboard-question-avatar-active mbsc-align-center mbsc-txt-m mbsc-bold mbsc-txt-muted";
        } else if (data.type === 'avatar' && width > 768 && (!active || active !== data.user)) {
            return "cs-dashboard-question-avatar";
        } else if (data.type === 'avatar' && width > 768 && active && active === data.user) {
            return "cs-dashboard-question-avatar cs-dashboard-question-avatar-active";
        } else if (data.type === 'avatar' && width < 768 && (!active || active !== data.user)) {
            return "cs-dashboard-question-avatar-xs";
        } else if (data.type === 'avatar' && width < 768 && active && active === data.user) {
            return "cs-dashboard-question-avatar-xs cs-dashboard-question-avatar-active-xs";
        } else if (data.type === 'ai' && width > 768 && Empty(toast)) {
            return "cs-dashboard-question-avatar";
        } else if (data.type === 'ai' && width < 768 && Empty(toast)) {
            return "cs-dashboard-question-avatar-xs";
        } else if (data.type === 'ai' && width > 768 && !Empty(toast)) {
            return "cs-dashboard-question-avatar cs-dashboard-question-avatar-pulse";
        } else if (data.type === 'ai' && width < 768 && !Empty(toast)) {
            return "cs-dashboard-question-avatar-xs cs-dashboard-question-avatar-pulse-xs";
        }
    }

    const UserActive = data => {
        setActive(data);

        setTimeout(() => {
            setActive(null);

        }, 1000);
    };

    const UpdateConversationReset = useCallback(data => {
        if (data.toast) {
            setMessages([]);
            setTokensUsed(0);
            setText('<p></p>');
            setFolded(false);
            setToast({message: locale.dashboardQuestion.z, color: 'info', display: 'bottom', duration: 1000});

            if (Empty(teamtalk)) {
                window.localStorage.text = '<p></p>';
            }
            Dialog({view: 'close'});
            window.scrollTo(0, 0);
        }
    }, [teamtalk, locale.dashboardQuestion.z, Dialog]);

    const UpdateWorkspaceReset = useCallback(data => {
        if (data.toast) {
            setWorkspace([]);
            setStack([]);
            setToast({message: locale.dashboardQuestion.w, color: 'info', display: 'bottom', duration: 1000});

            Dialog({view: 'close'});
            setFolded(false);
            Navigation({card: 'close'});
            window.scrollTo(0, 0);
        }
    }, [teamtalk, locale.dashboardQuestion.w, Navigation, Dialog]);

    const UpdateConversation = useCallback(data => {
        if (data.messages) {
            setMessages(data.messages);
            setText('<p></p>');
            if (Empty(teamtalk)) {
                window.localStorage.text = '<p></p>';
            }
        }
        if (data.avatar) {
            setAvatar(data.avatar);
        } else {
            setAvatar(null);
        }
        if (data.messages && data.messages.length !== 0 && data.messages[0].usage && data.messages[0].usage.tokensTotal) {
            setTokensUsed(data.messages[0].usage.tokensTotal);
        }
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        if (data.error) {
            alert({
                title: "Error",
                message: data.error,
                display: 'center'
            })
        }
        Navigation({card: 'close'});
        window.scrollTo(0, 0);
    }, [Navigation, sort]);

    const BroadcastTeamtalkQuestion = useCallback(data => {
        if (data.text && data.text !== text) {
            UserActive(data.user);
            broadcast.current = data.user;
            editorInstance.setData(data.text);
        }
    }, [editorInstance, text]);

    const BroadcastTeamtalkConversationUpdate = useCallback(data => {
        if (data.toast) {
            setToast({message: locale.dashboardQuestion.v, color: 'primary', display: 'center', duration: false});
        }
    }, [locale.dashboardQuestion.v]);

    const UpdateTeamtalkConversationUser = useCallback(data => {
        if (data.toast) {
            setToast({message: locale.dashboardQuestion.q, color: 'info', display: 'bottom', duration: 1000});
        }
        if (data.teamtalkUsers && data.teamtalkUsers.find(item => item.user === user.id)) {
            const item = {...teamtalk};
            item.users = data.teamtalkUsers;
            setTeamtalk(item);
        } else if (data.teamtalkUsers && !data.teamtalkUsers.find(item => item.user === user.id)) {
            setTeamtalk({});
            Conversation();
        }
    }, [locale.dashboardQuestion.q, Conversation, teamtalk, user.id]);

    useEffect(() => {
        if (wsResponse &&
            wsResponse &&
            !Empty(wsResponse) &&
            wsResponse.action === 'update' &&
            wsResponse.path === 'teamtalk-conversation-user' &&
            !Empty(teamtalk) &&
            wsResponse.data.teamtalk &&
            teamtalk.id === wsResponse.data.teamtalk) {
            UpdateTeamtalkConversationUser(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateTeamtalkConversationUser, setWsResponse]);

    useEffect(() => {
        if (wsResponse &&
            !Empty(wsResponse) &&
            wsResponse.action === 'broadcast' &&
            wsResponse.path === 'teamtalk-conversation-update' &&
            !Empty(teamtalk) &&
            wsResponse.data.teamtalk &&
            teamtalk.id === wsResponse.data.teamtalk) {
            BroadcastTeamtalkConversationUpdate(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, BroadcastTeamtalkConversationUpdate, setWsResponse, teamtalk]);

    useEffect(() => {
        if (!Empty(wsResponse) &&
            wsResponse.action === 'broadcast' &&
            wsResponse.path === 'teamtalk-question' &&
            !Empty(teamtalk) &&
            wsResponse.data.teamtalk &&
            teamtalk.id === wsResponse.data.teamtalk) {
            BroadcastTeamtalkQuestion(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, BroadcastTeamtalkQuestion, setWsResponse, teamtalk]);

    useEffect(() => {
        if (wsResponse &&
            !Empty(wsResponse) &&
            wsResponse.action === 'update' &&
            wsResponse.path === 'conversation-reset' &&
            ((Empty(teamtalk) && !wsResponse.data.teamtalk) ||
                (!Empty(teamtalk) && wsResponse.data.teamtalk && teamtalk.id === wsResponse.data.teamtalk))) {
            UpdateConversationReset(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateConversationReset, setWsResponse, teamtalk]);

    useEffect(() => {
        if (wsResponse &&
            !Empty(wsResponse) &&
            wsResponse.action === 'update' &&
            wsResponse.path === 'workspace-reset' &&
            ((Empty(teamtalk) && !wsResponse.data.teamtalk) ||
                (!Empty(teamtalk) && wsResponse.data.teamtalk && teamtalk.id === wsResponse.data.teamtalk))) {
            UpdateWorkspaceReset(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateWorkspaceReset, setWsResponse, teamtalk]);

    useEffect(() => {
        if (wsResponse &&
            !Empty(wsResponse) &&
            wsResponse.action === 'update' &&
            wsResponse.path === 'conversation') {
                if ((Empty(teamtalk) && !wsResponse.data.teamtalk && navigation.tab === wsResponse.data.tab) ||
                    (!Empty(teamtalk) && wsResponse.data.teamtalk && teamtalk.id === wsResponse.data.teamtalk)) {
                    UpdateConversation(wsResponse.data);
                    setWsResponse({});
                    setThinking(null);
                    setBroadcast(null);
                    setLocked(null);
                    setToast({});
                } else if (wsResponse.data.error) {
                    alert({
                        title: "Error",
                        message: wsResponse.data.error,
                        display: 'center'
                    })
                }
        }
    }, [wsResponse, UpdateConversation, setWsResponse, teamtalk, navigation.tab]);

    useEffect(() => {
        const match = configuration.mobi.model.find(m => m.value === model);
        setContextLimit(match?.context ?? null);
    }, [model]);

    return (
        <>
            {!folded &&
                <div className="mbsc-row cs-dashboard-question-header" >
                    <div className="mbsc-col" >
                        <div className="mbsc-row cs-dashboard-question-col-start">
                            {!Empty(teamtalk) &&
                                <Button onMouseEnter={() => setIconHover('plusminus')} onMouseLeave={() => setIconHover(null)} disabled={!Empty(toast) || system} color="primary" variant="flat" className="mbsc-bold cs-dashboard-question-color" onClick={() => Dialog({view: 'dashboardQuestionUsers'})} >
                                    <FontAwesomeIcon className="cs-dashboard-question-icon" icon={faPlusMinus} fade={iconHover === 'plusminus'} />{width > 768 && '\u00A0'}
                                </Button>
                            }
                            {!Empty(teamtalk) &&
                                <div title={teamtalk.personality} style={{backgroundImage: Avatar()}} className={AvatarStyle({type: 'ai', user: 'ai'})} />
                            }
                            {!Empty(teamtalk) && teamtalk.users && teamtalk.users.length !== 0 && teamtalk.users.map(item => {
                                return (item.avatar ?
                                        <div title={item.firstName + ' ' + item.lastName} style={{backgroundImage: "url(https://www.gravatar.com/avatar/" + item.avatar + "?d=robohash)"}} className={AvatarStyle({type: 'avatar', user: item.user})} />
                                        :
                                        <div title={item.firstName + ' ' + item.lastName} className={AvatarStyle({type: 'text', user: item.user})} >
                                            {item.firstName[0].toUpperCase()}{item.lastName[0].toUpperCase()}
                                        </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="mbsc-col-auto">
                        <div className="mbsc-row">
                            <Button onMouseEnter={() => setIconHover('settings')} onMouseLeave={() => setIconHover(null)} disabled={!Empty(toast)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-question-color" onClick={() => Dialog({view: 'dashboardQuestionSettings'})} >
                                <FontAwesomeIcon className="cs-dashboard-question-icon" icon={faSliders} fade={iconHover === 'settings'} />{width > 1200 && <div className="cs-dashboard-question-menu-text">{locale.dashboardQuestion.y}</div>}
                            </Button>
                            <Button onMouseEnter={() => setIconHover('arrow')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-conversation-color" onClick={() => ButtonFold()} >
                                <FontAwesomeIcon className="cs-dashboard-conversation-icon" icon={faArrowUpToLine} fade={iconHover === ('arrow')} />
                            </Button>
                        </div>
                    </div>
                </div>
            }
            {!folded &&
                <div className="mbsc-row cs-dashboard-question-main">
                    <div className="mbsc-col cs-dashboard-question-body">
                        <CKEditor
                            editor={BalloonEditor}
                            config={{
                                toolbar: {
                                    items: ['bold', 'italic', 'underline', 'subscript', 'superscript', 'removeFormat', '|', 'blockQuote', 'codeBlock'],
                                    shouldNotGroupWhenFull: false
                                },
                                blockToolbar: ['bold', 'italic', '|', 'blockQuote', 'codeBlock'],
                                versionCheck: false,
                                plugins: [
                                    Autosave,
                                    BlockQuote,
                                    BlockToolbar,
                                    Bold,
                                    Essentials,
                                    Italic,
                                    Paragraph,
                                    RemoveFormat,
                                    Subscript,
                                    Superscript,
                                    Underline,
                                    CodeBlock
                                ],
                                licenseKey: 'GPL',
                                ui: {
                                    poweredBy: {position:'inside', side:'right'}
                                },
                                placeholder: messages === 0 ? locale.dashboardQuestion.ah : locale.dashboardQuestion.aj
                            }}
                            data={text}
                            onChange={(event, editor) => EditorUpdate(editor.getData())}
                            disableWatchdog={true}
                            onReady={EditorReady}
                        />
                    </div>
                </div>
            }
            <div className={folded ? "mbsc-row cs-dashboard-question-footer-folded" : "mbsc-row cs-dashboard-question-footer"}>
                {((width > 476 && folded) || !folded) &&
                    <div className="mbsc-col" >
                        <div className="mbsc-row cs-dashboard-question-col-start">
                            <Button onMouseEnter={() => setIconHover('workspace')} onMouseLeave={() => setIconHover(null)} disabled={Boolean(!Empty(toast) || system)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-question-color" onClick={(ev) => ButtonWorkspace()} >
                                <div className="cs-dashboard-question-badge">
                                    <FontAwesomeIcon className="cs-dashboard-question-icon" icon={faObjectsColumn} beatFade={iconHover === 'workspace'} />
                                    {(workspace + stack) > 0 && (
                                        <span className="cs-dashboard-question-badge-icon">{(workspace + stack)}</span>
                                    )}
                                </div>
                                {width > 768 && <div className="cs-dashboard-question-menu-text">{locale.dashboardQuestion.f}</div>}
                            </Button>
                        </div>
                    </div>
                }
                <div className={width > 476 ? "mbsc-col-auto" : "mbsc-col"}>
                    <div className={width > 476 ? "mbsc-row" : "mbsc-row mbsc-justify-content-end"}>
                        {!folded &&
                            <Button onMouseEnter={() => setIconHover('send')} onMouseLeave={() => setIconHover(null)} disabled={!Empty(toast) || system || !text || text === '<p></p>' || quota} color="primary" variant="flat" className="mbsc-bold cs-dashboard-question-color" onClick={(ev) => ButtonMessage()} >
                                <FontAwesomeIcon className="cs-dashboard-question-icon" icon={faPaperPlane} beatFade={iconHover === 'send'} /><div className="cs-dashboard-question-menu-text">{locale.dashboardQuestion.a}</div>
                            </Button>
                        }
                        {folded && width < 476 &&
                            <Button onMouseEnter={() => setIconHover('workspace')} onMouseLeave={() => setIconHover(null)} disabled={Boolean(!Empty(toast) || system)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-question-color" onClick={(ev) => ButtonWorkspace()} >
                                <div className="cs-dashboard-question-badge">
                                    <FontAwesomeIcon className="cs-dashboard-question-icon" icon={faObjectsColumn} beatFade={iconHover === 'workspace'} />
                                    {(workspace + stack) > 0 && (
                                        <span className="cs-dashboard-question-badge-icon">{(workspace + stack)}</span>
                                    )}
                                </div>
                            </Button>
                        }
                        {folded &&
                            <Button onMouseEnter={() => setIconHover('question')} onMouseLeave={() => setIconHover(null)} disabled={!Empty(toast) || system} color="primary" variant="flat" className="mbsc-bold cs-dashboard-question-color" onClick={(ev) => ButtonQuestion()} >
                                <FontAwesomeIcon className="cs-dashboard-question-icon" icon={faMessageLines} beatFade={iconHover === 'question'} />{width > 375 && <div className="cs-dashboard-question-menu-text">{width < 1200 ? locale.dashboardQuestion.l : locale.dashboardQuestion.t}</div>}
                            </Button>
                        }
                        {folded &&
                            <Button onMouseEnter={() => setIconHover('settings')} onMouseLeave={() => setIconHover(null)} disabled={!Empty(toast)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-question-color" onClick={() => Dialog({view: 'dashboardQuestionSettings'})} >
                                <FontAwesomeIcon className="cs-dashboard-question-icon" icon={faSliders} fade={iconHover === 'settings'} />
                            </Button>
                        }
                        {folded &&
                            <Button onMouseEnter={() => setIconHover('arrow')} onMouseLeave={() => setIconHover(null)} disabled={!Empty(toast) || system} color="primary" variant="flat" className="mbsc-bold cs-dashboard-conversation-color" onClick={() => ButtonFold()} >
                                <FontAwesomeIcon className="cs-dashboard-conversation-icon" icon={faArrowDownFromLine} fade={iconHover === ('arrow')} />{width <= 576 && '\u00A0'}
                            </Button>
                        }
                    </div>
                </div>
            </div>
            {dialog.view === 'dashboardQuestionSettings' &&
                <DashboardQuestionSettings
                    Dialog={Dialog}
                    dialog={dialog}
                    locale={locale}
                    configuration={configuration}
                    width={width}
                    toast={toast}
                    system={system}
                    setSystem={setSystem}
                    teamtalk={teamtalk}
                    navigation={navigation}
                    setModel={setModel}
                    setTeamtalk={setTeamtalk}
                    setToast={setToast}
                    messages={messages}
                    workspace={workspace}
                    functions={functions}
                    setFunctions={setFunctions}
                    setLabel={setLabel}
                />
            }
            {dialog.view === 'dashboardQuestionSave' &&
                <DashboardQuestionSave
                    dialog={dialog}
                    Dialog={Dialog}
                    locale={locale}
                    setToast={setToast}
                    width={width}
                    navigation={navigation}
                />
            }
            {dialog.view === 'dashboardQuestionUsers' &&
                <DashboardQuestionUsers
                    Dialog={Dialog}
                    dialog={dialog}
                    locale={locale}
                    width={width}
                    teamtalk={teamtalk}
                />
            }
            <Snackbar
                cssClass="cs-dashboard-question-snackbar mbsc-bold"
                isOpen={system}
                message={locale.dashboardQuestion.b}
                duration={false}
                color="primary"
                display="bottom"
                button={{
                    text: locale.dashboardQuestion.c,
                    action: () => {
                        ButtonSystem()
                    }
                }}
            />
            <Toast
                isOpen={memoryIndicator}
                message={memoryUsage}
                duration={false}
                color={usageTier}
                display="bottom"
            />
        </>
    );
}

export default DashboardQuestion;
